import React, { useEffect, useMemo, useRef, useState, useTransition } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Alert, IconButton, Paper, Stack, Tab, Tabs } from '@mui/material';

import CopyOfferModal from '~/components/Offers/Edit/CopyOfferModal';

import { CONTENT, IMAGES } from '~/consts/offerFields';

import { useGetProperty } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetPackageInclusionsByOffer } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';
import { useGetOffer, useGetOfferSchema } from '~/hooks/apis/offers/useGetOffer';

import { copyImage, getUploadedImageId } from '~/services/ImageService';
import OffersService from '~/services/OffersService';

import { isLTLEHotelOfferType } from '~/utils/offer';

import DefaultContent from './DefaultContent';
import type { CopiedOfferContent } from './DefaultContent/types';
import OfferBuilderImagesVideo from './ImagesVideo';
import Inclusions from './Inclusions';
import { type MasterInclusion, buildMasterInclusionsFromOffer, preserveMasterInclusionOrder } from './Inclusions/utils';
import LazyContentTabPanel from './LazyContentTabPanel';
import OfferPackages from './OfferPackages';
import PropertyInformation, { type OfferBuilderProperty } from './PropertyInformation';
import OfferBuilderRooms from './Rooms';
import { getOfferRoomTypes } from './Rooms/utils';
import TagsAndMarketing from './TagsAndMarketing';
import useOfferBuilderMedia from './useOfferBuilderMedia';

const DEFAULT_CONTENT_TAB = 'Default content';
const IMAGES_VIDEO_TAB = 'Images & Video';
const INCLUSIONS_TAB = 'Inclusions';
const OFFER_PACKAGES_TAB = 'Offer packages';
const PROPERTY_INFORMATION_TAB = 'Property information';
const ROOMS_TAB = 'Rooms';
const TAGS_AND_MARKETING_TAB = 'Tags & Marketing';

const CONTENT_TABS = [
  PROPERTY_INFORMATION_TAB,
  ROOMS_TAB,
  OFFER_PACKAGES_TAB,
  INCLUSIONS_TAB,
  DEFAULT_CONTENT_TAB,
  TAGS_AND_MARKETING_TAB,
  IMAGES_VIDEO_TAB,
];

interface CopiedOfferData extends CopiedOfferContent {
  images?: Array<App.OfferImage>;
  packages?: Array<App.Package>;
}

function getInitialContentTab(search: string) {
  const contentTab = new URLSearchParams(search).get('contentTab');

  return contentTab && CONTENT_TABS.includes(contentTab) ? contentTab : DEFAULT_CONTENT_TAB;
}

export default function OfferBuilderContentPage() {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(() => getInitialContentTab(location.search));
  const [activeContentTab, setActiveContentTab] = useState(() => getInitialContentTab(location.search));
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false);
  const [copiedOfferContent, setCopiedOfferContent] = useState<CopiedOfferContent>();
  const [copiedOfferPackages, setCopiedOfferPackages] = useState<Array<App.Package>>();
  const [packageCopyVersion, setPackageCopyVersion] = useState(0);
  const [savedOfferStatus, setSavedOfferStatus] = useState<string>();
  const [draftMasterInclusions, setDraftMasterInclusions] = useState<Array<MasterInclusion>>([]);
  const [savedMasterInclusions, setSavedMasterInclusions] = useState<Array<MasterInclusion>>([]);
  const hasUserEditedInclusionsRef = useRef(false);
  const initialisedInclusionsOfferIdRef = useRef<string | undefined>(undefined);
  const [, startTabChangeTransition] = useTransition();

  const {
    data: offer,
    isLoading: isLoadingOffer,
    error: offerError,
    refetch: refetchOffer,
  } = useGetOffer(offerId, {
    retain_dynamic_tags: true,
  });
  const { data: offerSchema } = useGetOfferSchema(offerId);
  const isSupportedOfferType = !!offer?.type && isLTLEHotelOfferType(offer.type);
  const hasOfferLoadError = !isLoadingOffer && !!offerError && !offer;
  const shouldRenderContentTabs = isLoadingOffer || (!!offer && isSupportedOfferType);
  const effectivePackages = copiedOfferPackages ?? offer?.packages;
  const propertyId = isSupportedOfferType
    ? effectivePackages?.find((offerPackage) => offerPackage?.fk_property_id)?.fk_property_id
    : undefined;
  const vendorId = offer?.vendor_account_id;
  const currentOfferStatus = savedOfferStatus ?? offer?.status;
  const { data: packageInclusions, isLoading: isLoadingInclusions } = useGetPackageInclusionsByOffer({
    offerId,
    enabled: shouldRenderContentTabs,
  });
  const offerInclusions = useMemo(() => buildMasterInclusionsFromOffer(packageInclusions), [packageInclusions]);

  const {
    data: property,
    isLoading: isLoadingProperty,
    error: propertyError,
  } = useGetProperty<OfferBuilderProperty>({
    propertyId,
    enabled: isSupportedOfferType,
  });

  const hotelName = property?.name || propertyId || 'Property';
  const offerRoomTypes = useMemo(
    () => getOfferRoomTypes(property?.room_types ?? [], effectivePackages ?? []),
    [effectivePackages, property?.room_types],
  );
  const media = useOfferBuilderMedia({
    offerId,
    images: offer?.images,
    propertyRoomTypes: offerRoomTypes,
    videos: offer?.videos,
  });
  const showContentTab = (tab: string) => {
    setSelectedTab(tab);
    startTabChangeTransition(() => setActiveContentTab(tab));
  };

  const hideCopyModal = () => {
    setIsCopyModalVisible(false);
  };

  useEffect(() => {
    setSavedOfferStatus(offer?.status);
  }, [offer?.status]);

  const copyImagesToOffer = async (images?: Array<App.OfferImage>) => {
    if (!images?.length) {
      return;
    }

    const copiedImages = [];
    for (const image of images) {
      const copyImageResponse = await copyImage(image.id_cloudinary_external);
      const copiedImageId = getUploadedImageId(copyImageResponse);

      if (!copiedImageId) {
        throw new Error('Image copy did not return an ID');
      }

      copiedImages.push({
        filename: image.filename,
        id_cloudinary_external: copiedImageId,
      });
    }

    const existingImageIds = offer?.images?.map((image) => image.id_image) ?? [];

    for (const image of copiedImages) {
      await OffersService.createImage(image, offerId);
    }

    for (const imageId of existingImageIds) {
      await OffersService.deleteImage(offerId, imageId);
    }

    await refetchOffer();
    enqueueSnackbar('Offer images copied', { variant: 'success' });
  };

  const copyOfferData = async (copiedOfferData: CopiedOfferData, selectedCopyOptions: Array<string>) => {
    const nextCopiedOfferContent = { ...copiedOfferData };

    if (selectedCopyOptions.includes(IMAGES)) {
      delete nextCopiedOfferContent.images;
      try {
        await copyImagesToOffer(copiedOfferData.images);
      } catch {
        enqueueSnackbar('Image copy failed. Content and packages were still copied into the draft.', {
          variant: 'warning',
        });
      }
    }

    if (copiedOfferData.packages) {
      setCopiedOfferPackages(copiedOfferData.packages);
      setPackageCopyVersion((version) => version + 1);
      enqueueSnackbar('Copied offer packages into the draft', { variant: 'success' });
    }

    if (selectedCopyOptions.includes(CONTENT)) {
      setCopiedOfferContent(nextCopiedOfferContent);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (!searchParams.has('contentTab')) {
      return;
    }

    searchParams.delete('contentTab');
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (isLoadingInclusions) {
      return;
    }

    if (initialisedInclusionsOfferIdRef.current !== offerId) {
      initialisedInclusionsOfferIdRef.current = offerId;
      hasUserEditedInclusionsRef.current = false;
      setDraftMasterInclusions(offerInclusions);
      setSavedMasterInclusions(offerInclusions);
      return;
    }

    setSavedMasterInclusions((currentInclusions) => preserveMasterInclusionOrder(currentInclusions, offerInclusions));

    if (!hasUserEditedInclusionsRef.current) {
      setDraftMasterInclusions((currentInclusions) => preserveMasterInclusionOrder(currentInclusions, offerInclusions));
    }
  }, [isLoadingInclusions, offerId, offerInclusions]);

  const handleMasterInclusionsChange: React.Dispatch<React.SetStateAction<Array<MasterInclusion>>> = (nextValue) => {
    hasUserEditedInclusionsRef.current = true;
    setDraftMasterInclusions(nextValue);
  };

  const handleMasterInclusionsSaved = (nextMasterInclusions: Array<MasterInclusion>) => {
    hasUserEditedInclusionsRef.current = false;
    setDraftMasterInclusions(nextMasterInclusions);
    setSavedMasterInclusions(nextMasterInclusions);
  };

  return (
    <>
      <Helmet>
        <title>Offer Builder | {hotelName}</title>
      </Helmet>

      {hasOfferLoadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to load offer details.
        </Alert>
      )}

      {!isLoadingOffer && offer && !isSupportedOfferType && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Offer Builder is only available for flash offers.
        </Alert>
      )}

      {isLoadingOffer && <Alert severity="info">Loading Offer Builder content.</Alert>}

      {!isLoadingOffer && shouldRenderContentTabs && (
        <>
          <Paper sx={{ position: 'sticky', top: 0, zIndex: 2, mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Tabs
                value={selectedTab}
                onChange={(_event, value) => showContentTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ flex: 1, minWidth: 0 }}
              >
                {CONTENT_TABS.map((tab) => (
                  <Tab key={tab} value={tab} label={tab} />
                ))}
              </Tabs>
              <IconButton
                onClick={() => setIsCopyModalVisible(true)}
                size="large"
                color="primary"
                aria-label="Copy Offer"
              >
                <ContentCopyIcon />
              </IconButton>
            </Stack>
          </Paper>
          <CopyOfferModal
            isCopyModalVisible={isCopyModalVisible}
            setAsNotReady={() => undefined}
            hideCopyModal={hideCopyModal}
            copyOfferData={copyOfferData}
            currentOfferStatus={currentOfferStatus}
            originalOfferDetails={offer}
          />

          <LazyContentTabPanel activeTab={activeContentTab} tab={PROPERTY_INFORMATION_TAB}>
            <PropertyInformation
              property={property}
              vendorId={vendorId}
              isLoadingProperty={isLoadingOffer || isLoadingProperty}
              offerError={offerError}
              propertyError={propertyError}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={ROOMS_TAB}>
            <OfferBuilderRooms
              media={media}
              offerPackages={effectivePackages}
              isLoadingProperty={isLoadingOffer || isLoadingProperty}
              onOpenImagesVideo={() => showContentTab(IMAGES_VIDEO_TAB)}
              propertyError={propertyError}
              propertyId={propertyId}
              propertyName={hotelName}
              propertyRoomTypes={offerRoomTypes}
              vendorId={vendorId}
              offerType={offer?.type}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={OFFER_PACKAGES_TAB}>
            <OfferPackages
              offerId={offerId}
              masterInclusions={isLoadingInclusions ? undefined : savedMasterInclusions}
              packageInclusions={packageInclusions}
              packageCopyVersion={packageCopyVersion}
              packages={effectivePackages}
              property={property}
              propertyRoomTypes={property?.room_types}
              vendorId={vendorId}
              offerType={offer?.type}
              isLoadingOffer={isLoadingOffer}
              offerError={offerError}
              onOpenInclusions={() => showContentTab(INCLUSIONS_TAB)}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={INCLUSIONS_TAB}>
            <Inclusions
              inclusions={draftMasterInclusions}
              isLoadingInclusions={isLoadingInclusions}
              offerId={offerId}
              packageInclusions={packageInclusions}
              packages={effectivePackages}
              savedInclusions={savedMasterInclusions}
              vendorId={vendorId}
              onChange={handleMasterInclusionsChange}
              onSaved={handleMasterInclusionsSaved}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={DEFAULT_CONTENT_TAB}>
            <DefaultContent
              offerAdditionalDescription={offer?.additional_description}
              offerDescription={offer?.description}
              offerFacilities={offer?.facilities}
              offerFinePrint={offer?.fine_print}
              offerFlightDestinationPort={offer?.flight_destination_port}
              offerFlightsEnabled={offer?.flights_enabled}
              offerGettingThere={offer?.getting_there}
              offerHighlights={offer?.highlights}
              offerInclusions={offer?.offer_inclusions}
              offerName={offer?.name}
              offerPreheader={offer?.preheader}
              offerSaleUnit={offer?.sale_unit}
              offerSlug={offer?.slug}
              offerStatus={offer?.status}
              offerSubjectLine={offer?.subject_line}
              offerTileInclusionHeading={offer?.offer_tile_inclusion_heading}
              offerType={offer?.type}
              copiedOfferContent={copiedOfferContent}
              packages={effectivePackages}
              vendorId={vendorId}
              onOfferStatusSaved={setSavedOfferStatus}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={TAGS_AND_MARKETING_TAB}>
            <TagsAndMarketing
              campaigns={offer?.campaigns}
              holidayTypes={offer?.holiday_types}
              locations={offer?.locations}
              offerId={offerId}
              offerSchema={offerSchema}
              offerType={offer?.type}
            />
          </LazyContentTabPanel>
          <LazyContentTabPanel activeTab={activeContentTab} tab={IMAGES_VIDEO_TAB}>
            <OfferBuilderImagesVideo
              media={media}
              offerId={offerId}
              offerSlug={offer?.slug}
              offerType={offer?.type}
              propertyId={property?.id}
              propertyLogoId={property?.logo_id_cloudinary_external}
              propertyRoomTypes={offerRoomTypes}
            />
          </LazyContentTabPanel>
        </>
      )}
    </>
  );
}
